import {
  downloadSectionsAsPdf,
  type PdfSection,
} from '@utils/downloadHtmlAsPdf';
import { useCallback, useEffect, useRef, useState } from 'react';
import { captureSections, PDF_SECTION_ATTR } from './calculator.pdf';
import type { CalculatorState } from './useCalculator';

export type CalculatorPdfError = 'generate' | 'save';

export const useCalculatorPdf = ({
  calculator,
  title,
}: {
  calculator: CalculatorState;
  title: string;
}) => {
  const [captureSnapshot, setCaptureSnapshot] =
    useState<CalculatorState | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSections, setPreviewSections] = useState<PdfSection[]>([]);
  const [filename, setFilename] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<CalculatorPdfError | null>(null);
  const pdfInputRef = useRef<HTMLDivElement>(null);
  const pdfResultsRef = useRef<HTMLDivElement>(null);
  const inFlightRef = useRef(false);

  const download = () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setError(null);
    setCaptureSnapshot({ ...calculator });
  };

  const closePreview = useCallback(() => {
    if (isSaving) return;
    setPreviewOpen(false);
    setPreviewSections([]);
    setFilename('');
    setError(null);
  }, [isSaving]);

  const savePdf = useCallback(async () => {
    const trimmedFilename = filename.trim();
    if (!trimmedFilename || previewSections.length === 0 || isSaving) return;

    setIsSaving(true);
    setError(null);
    try {
      await downloadSectionsAsPdf(previewSections, trimmedFilename);
      setPreviewOpen(false);
      setPreviewSections([]);
      setFilename('');
    } catch {
      setError('save');
    } finally {
      setIsSaving(false);
    }
  }, [filename, isSaving, previewSections]);

  useEffect(() => {
    if (!captureSnapshot) return;
    let cancelled = false;

    const capture = async () => {
      await new Promise((r) => requestAnimationFrame(r));

      try {
        if (cancelled) return;

        if (!pdfInputRef.current || !pdfResultsRef.current) {
          setError('generate');
          return;
        }

        const inputSections = Array.from(
          pdfInputRef.current.querySelectorAll<HTMLElement>(
            `[${PDF_SECTION_ATTR}]`,
          ),
        );
        const resultSections = Array.from(
          pdfResultsRef.current.querySelectorAll<HTMLElement>(
            `[${PDF_SECTION_ATTR}]`,
          ),
        );
        const sections = await captureSections(
          [...inputSections, ...resultSections],
          { pageBreakBeforeIndex: inputSections.length },
        );

        if (cancelled) return;

        setPreviewSections(sections);
        setFilename(title);
        setPreviewOpen(true);
      } catch {
        if (!cancelled) setError('generate');
      } finally {
        if (!cancelled) {
          inFlightRef.current = false;
          setCaptureSnapshot(null);
        }
      }
    };

    capture();
    return () => {
      cancelled = true;
    };
  }, [captureSnapshot, title]);

  return {
    captureSnapshot,
    closePreview,
    download,
    error,
    filename,
    isGenerating: !!captureSnapshot,
    isSaving,
    pdfInputRef,
    pdfResultsRef,
    previewOpen,
    previewSections,
    savePdf,
    setFilename,
  };
};
